import PropTypes from 'prop-types';
import { AnimatePresence, m, useReducedMotion } from 'framer-motion';
import { useEffect } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import {
  Backdrop,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
// hooks
import useSettings from '../../../hooks/useSettings';
import useCollapseSettingDrawer from 'src/hooks/useCollapseSettingDrawer';
// config
import { defaultSettings } from '../../../config';
// universe
import Icon from '../../universe/Icon';
import {
  HIT,
  MOTION,
  RADIUS,
  UNIVERSE_FONT,
  hairlineOf
} from '../../../theme/universe/brand';
import { iconButtonSx } from '../../../theme/universe/buttonSx';
//
import SettingMode from './SettingMode';
import SettingLayout from './SettingLayout';
// import SettingStretch from './SettingStretch';
// import SettingContrast from './SettingContrast';
// import SettingDirection from './SettingDirection';
// import SettingFullscreen from './SettingFullscreen';
// import SettingColorPresets from './SettingColorPresets';

// ----------------------------------------------------------------------
// THE SETTINGS PANEL (DESIGN.md §9, §18, §23): a temporary layer laid over
// the page from the right, so it is the system's GLASS — the measured fill,
// a 12px blur, the hairline and `shadow.lg`, radius 12 — and it slides in
// on the spatial curve and leaves on the exit one (§11), never bounces. Its
// head is a dialog's (title, the reset, the close); its two settings are
// named by a label each and drawn as the system draws a choice: the mode as
// the segmented control, the sidebar as two tiles that show what they do.
//
// The "Coming Soon" illustration it used to carry filled half the panel with
// a picture; a picture that fills space is decoration (§27). One quiet line
// at the foot says the same thing.

const PANEL_WIDTH = 320;
const isLight = (theme) => theme.palette.mode === 'light';

const glassSx = (theme) =>
  isLight(theme)
    ? {
        bgcolor: 'rgba(255,255,255,0.88)',
        backdropFilter: 'blur(12px) saturate(140%)',
        WebkitBackdropFilter: 'blur(12px) saturate(140%)',
        border: '1px solid rgba(1,38,109,0.12)',
        boxShadow: '0 16px 40px -8px rgba(1,38,109,.20)'
      }
    : {
        bgcolor: 'rgba(17,27,51,0.84)',
        backdropFilter: 'blur(12px) saturate(120%)',
        WebkitBackdropFilter: 'blur(12px) saturate(120%)',
        border: '1px solid rgba(255,255,255,0.10)',
        boxShadow:
          '0 20px 48px -10px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.08)'
      };

Section.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  hint: PropTypes.string,
  children: PropTypes.node
};

/** One setting: its label (13/600), a quiet line when it needs one, the control. */
function Section({ id, label, hint, children }) {
  return (
    <Box>
      <Typography
        id={id}
        component="h3"
        sx={{
          m: 0,
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1.3,
          color: 'text.primary'
        }}
      >
        {label}
      </Typography>
      {hint && (
        <Typography
          sx={{
            mt: 0.25,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.4,
            color: 'text.secondary'
          }}
        >
          {hint}
        </Typography>
      )}
      <Box sx={{ mt: 1.25 }}>{children}</Box>
    </Box>
  );
}

// ----------------------------------------------------------------------

export default function SettingsDrawer() {
  const theme = useTheme();
  const still = useReducedMotion();
  const {
    themeMode,
    themeLayout,
    themeStretch,
    themeContrast,
    themeDirection,
    themeColorPresets,
    viewNavbar,
    onResetSetting
  } = useSettings();
  const {
    collapseClickSetting: open,
    onToggleCollapseSetting: onToggleCollapse
  } = useCollapseSettingDrawer();

  const notDefault =
    themeMode !== defaultSettings.themeMode ||
    themeLayout !== defaultSettings.themeLayout ||
    themeStretch !== defaultSettings.themeStretch ||
    themeContrast !== defaultSettings.themeContrast ||
    themeDirection !== defaultSettings.themeDirection ||
    themeColorPresets !== defaultSettings.themeColorPresets ||
    viewNavbar !== defaultSettings.viewNavbar;

  const rtl = themeDirection === 'rtl';
  const away = rtl ? -32 : 32;

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [open]);

  const handleClose = () => {
    onToggleCollapse();
  };

  // Esc closes the top layer (§24).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onToggleCollapse();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onToggleCollapse]);

  return (
    <>
      <Backdrop
        open={open}
        onClick={handleClose}
        sx={{
          background: 'transparent',
          zIndex: (t) => t.zIndex.drawer + 1
        }}
      />

      <AnimatePresence>
        {open && (
          <Box
            component={m.div}
            role="dialog"
            aria-label="Settings"
            initial={{ x: still ? 0 : away, opacity: 0 }}
            animate={{
              x: 0,
              opacity: 1,
              transition: {
                duration: MOTION.spatial / 1000,
                ease: [0.32, 0.72, 0, 1]
              }
            }}
            exit={{
              x: still ? 0 : away,
              opacity: 0,
              transition: {
                duration: MOTION.exit / 1000,
                ease: [0.4, 0, 1, 1]
              }
            }}
            sx={{
              // as tall as what it holds, never a column of empty glass
              position: 'fixed',
              top: 0,
              maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 32px)' },
              ...(rtl ? { left: 0 } : { right: 0 }),
              m: { xs: 1, sm: 2 },
              width: { xs: 'calc(100% - 16px)', sm: PANEL_WIDTH },
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              zIndex: theme.zIndex.drawer + 3,
              borderRadius: `${RADIUS.overlay}px`,
              ...glassSx(theme),
              '@supports not (backdrop-filter: blur(1px))': {
                bgcolor: 'background.paper'
              },
              '@media (prefers-reduced-transparency: reduce)': {
                bgcolor: 'background.paper',
                backdropFilter: 'none'
              },
              '& .MuiTypography-root, & button': { fontFamily: UNIVERSE_FONT }
            }}
          >
            {/* the head: the title, the reset, the close */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{
                pl: 2.5,
                pr: 1.5,
                py: 1.5,
                borderBottom: `1px solid ${hairlineOf(theme)}`
              }}
            >
              <Typography
                component="h2"
                sx={{
                  m: 0,
                  flexGrow: 1,
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: 'text.primary'
                }}
              >
                Settings
              </Typography>

              <Tooltip
                arrow
                title={
                  notDefault ? 'Reset to the defaults' : 'Already the defaults'
                }
              >
                <span>
                  <IconButton
                    onClick={onResetSetting}
                    disabled={!notDefault}
                    aria-label="Reset to the defaults"
                    sx={{
                      ...iconButtonSx(theme, 36),
                      '&.Mui-disabled': { color: 'text.disabled' }
                    }}
                  >
                    <Icon name="Refresh2" size="md" />
                  </IconButton>
                </span>
              </Tooltip>

              <IconButton
                onClick={handleClose}
                aria-label="Close the settings"
                sx={{
                  ...iconButtonSx(theme, 36),
                  '@media (pointer: coarse)': {
                    width: HIT.touch,
                    height: HIT.touch
                  }
                }}
              >
                <Icon name="CloseCircle" size="md" />
              </IconButton>
            </Stack>

            {/* the settings */}
            <Stack spacing={3} sx={{ minHeight: 0, overflowY: 'auto', p: 2.5 }}>
              <Section id="settings-mode-label" label="Mode">
                <SettingMode />
              </Section>
              <Section
                id="settings-layout-label"
                label="Layout"
                hint="How wide the sidebar is"
              >
                <SettingLayout />
              </Section>
              {/* <Section label="Contrast"><SettingContrast /></Section>
              <Section label="Direction"><SettingDirection /></Section>
              <Section label="Presets"><SettingColorPresets /></Section>
              <Section label="Stretch"><SettingStretch /></Section>
              <SettingFullscreen /> */}
            </Stack>

            {/* what the "Coming Soon" picture used to say, in one line */}
            <Typography
              sx={{
                px: 2.5,
                py: 1.75,
                borderTop: `1px solid ${hairlineOf(theme)}`,
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.4,
                color: 'text.secondary',
                textAlign: 'center'
              }}
            >
              More settings are on the way.
            </Typography>
          </Box>
        )}
      </AnimatePresence>
    </>
  );
}
